import React from 'react';
import cx from 'classnames';

import { FieldRenderProps } from '../types';
import { InputTitle } from '../../components/Input';
/**
 * Text Field component. It follows the props from react-final-form
 */
interface IInputText extends FieldRenderProps<HTMLElement> {
  /** Optional autocomplete */
  autoComplete?: string;
  /** Passed down classname. */
  className?: string;
  /** Optional id indicator */
  id?: string;
  /** Is disabled */
  isDisabled?: boolean;
  /** Optional inputType (mainly for number, email) */
  inputType?: string;
  /** If background has a solid color */
  isReverse?: boolean;
  /** Hide the number type default buttons */
  isWithoutNumberControls?: boolean;
  /** Is small */
  isSmall?: boolean;
  /** Optional placeholder */
  placeholder?: string;
  /** step for number. for example, 0.1*/
  step?: string;
  /** Optional title */
  title?: string;
  /** Option test id */
  testId?: string;
}

export const InputText: React.FC<IInputText> = ({
  className,
  id,
  input: { name, onBlur, onChange, onFocus, value },
  isSmall = false,
  isDisabled = false,
  isReverse = false,
  isWithoutNumberControls = true,
  inputType = 'text',
  meta: { active, error, submitError, submitting, touched, invalid, dirtySinceLastSubmit },
  placeholder,
  step,
  title,
  testId,
  autoComplete,
}) => {
  const errorMsg = error || submitError;
  const isValidationError = invalid && touched && !!error;
  const isSubmitError = !!invalid && !dirtySinceLastSubmit && !!submitError;
  const isError = (isValidationError || isSubmitError) && !submitting;
  const combinedContainerClass = cx({
    'panda-input-container': true,
    [className || '']: !!className,
  });
  const combinedInputClass = cx({
    'panda-input-text': true,
    'panda-input--small': isSmall,
    'panda-input--large': !isSmall,
    'panda-typography--P2': isSmall,
    'panda-typography--P1': !isSmall,
    'panda-input-text--focused': !!active,
    'panda-input-text--error': isError,
    'panda-input-text--disabled': isDisabled,
    'panda-input-text--withoutNumber': inputType === 'number' && isWithoutNumberControls,
  });
  const combinedErrorClass =
    !!isError &&
    cx({
      'panda-input-error': true,
      'panda-input-error--small': isSmall,
    });

  return (
    <div className={combinedContainerClass}>
      {!!title && <InputTitle isReverse={isReverse} isSmall={isSmall} title={title} />}
      <input
        autoComplete={autoComplete}
        className={combinedInputClass}
        disabled={isDisabled || submitting}
        id={id}
        name={name}
        onBlur={onBlur}
        onChange={onChange}
        onFocus={onFocus}
        placeholder={placeholder}
        step={step}
        type={inputType}
        data-test={testId}
        value={value}
      />
      {!!isError && <p className={combinedErrorClass || ''}>{errorMsg}</p>}
    </div>
  );
};
